import { createStyles, Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import laqusImf from 'assets/laqus-imf-digital-1x.png';
import { NovaSenhaRequestResponse } from 'common/genericTypes';
import history from 'components/history';
import { ComponentLoader } from 'components/loader';
import { Button, MdSpace, SmSpace, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { RootState } from 'store';

interface RouteParams {
  requestId: string;
}

const useStyles = makeStyles(() =>
  createStyles({
    paper: {
      boxShadow: 'none',
      padding: '40px',
      border: '1px solid #E5E5E5',
      maxWidth: 580,
      position: 'relative',
    },
    MuiIcon: {
      fill: '#E5E5E5',
      marginRight: 10,
    },
    MuiIconActive: {
      fill: '#00E08E',
      marginRight: 10,
    },
    loadingDiv: {
      width: '500px',
      height: '754px',
      backgroundColor: 'white',
      position: 'absolute',
      zIndex: 50,
    },
  })
);

const mapStateToProps = (state: RootState) => ({
  usuarioApi: state.usuarioApi,
});

const NovaSenha: React.FC<RouteComponentProps<RouteParams> & ReturnType<typeof mapStateToProps>> = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    usuarioApi: { putNovaSenhaRequest, getNovaSenhaRequest },
  } = props;
  const { requestId } = props.match.params;

  const [senha, setSenha] = useState<string>('');
  const [senhaConfirm, setSenhaConfirm] = useState<string>('');
  const [request, setRequest] = useState<NovaSenhaRequestResponse>();
  const [allowRequest, setAllowRequest] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const handleSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (request) {
        const result = await putNovaSenhaRequest(dispatch, request.id, senha);
        if (result) history.push('/');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getRequest = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getNovaSenhaRequest(dispatch, requestId);
      if (result) {
        setAllowRequest(true);
        setRequest(result);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [dispatch, getNovaSenhaRequest, requestId]);

  useEffect(() => {
    getRequest();
  }, [getRequest]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ margin: '40px 0' }}>
          <Paper className={classes.paper}>
            {loading && (
              <div className={classes.loadingDiv}>
                <ComponentLoader />
              </div>
            )}
            <Grid container justify="center">
              <Grid item>
                <img src={laqusImf} alt="Logo Laqus IMF digital" />
              </Grid>
              <Grid item sm={12}>
                <MdSpace />
                <Typography align="center" variant="h2">
                  {allowRequest ? `Seja bem-vindo!` : `Olá!`}
                </Typography>
                <MdSpace />
                {allowRequest ? (
                  <Typography align="center" variant="body2">
                    {`Preencha os dados abaixo para cadastrar uma nova senha.`}
                  </Typography>
                ) : (
                  <>
                    <Typography align="center" variant="body2">
                      {`Este link não existe ou já expirou.`}
                      <br />
                      <a style={{ color: 'inherit' }} href="/login">
                        {'Clique aqui'}
                      </a>
                      &nbsp;{`para ser redirecionado para o login.`}
                    </Typography>
                    <SmSpace />
                    <Typography align="center" variant="body2">
                      {`Caso isso seja um problema, envie um e-mail para`}&nbsp;
                      <a style={{ color: '#00E08E' }} href="mailto:admin@admin.com">
                        {'admin@admin.com'}
                      </a>
                    </Typography>
                  </>
                )}
                <MdSpace />
              </Grid>
              <Grid item sm={12}>
                {allowRequest && (
                  <form onSubmit={handleSubmit}>
                    <TextField
                      label="Nova Senha"
                      type="password"
                      required
                      value={senha}
                      onChange={(value) => setSenha(value)}
                    />
                    <SmSpace />
                    <TextField
                      label="Confirmação da Nova Senha"
                      type="password"
                      required
                      value={senhaConfirm}
                      onChange={(value) => setSenhaConfirm(value)}
                    />
                    <MdSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={senha === senhaConfirm ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`As senhas devem ser iguais`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={senha.match(/.{10,}/) ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`Mínimo 10 caracteres`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={senha.match(/[A-Z]{1,}/) ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`Letra maíuscula`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={senha.match(/[0-9]{1,}/) ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`Algarismo`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon
                        className={senha.match(/[^a-zA-Z 0-9]+/g) ? classes.MuiIconActive : classes.MuiIcon}
                      />
                      <Typography align="left" variant="body2">{`Caractere especial`}</Typography>
                    </Grid>
                    <SmSpace />
                    <Grid container direction="row">
                      <CheckCircleIcon className={!senha.match(/(.)\1+/) ? classes.MuiIconActive : classes.MuiIcon} />
                      <Typography align="left" variant="body2">{`Não possuir 2 caracteres seguidos iguais`}</Typography>
                    </Grid>
                    <MdSpace />
                    <Grid>
                      <Button
                        type="submit"
                        disabled={
                          !senha ||
                          !senha.match(/.{8,}/) ||
                          !senha.match(/[A-Z]{1,}/) ||
                          !senha.match(/[0-9]{1,}/) ||
                          !senha.match(/[^a-zA-Z 0-9]+/g) ||
                          !!senha.match(/(.)\1+/) ||
                          senha !== senhaConfirm
                        }
                        variant="contained"
                        color="primary"
                        style={{ margin: '0 auto', display: 'block' }}
                      >
                        {'Enviar'}
                      </Button>
                    </Grid>
                  </form>
                )}
              </Grid>
            </Grid>
          </Paper>
        </div>
      </div>
    </>
  );
};

export default connect(mapStateToProps)(NovaSenha);
